'use client';

/* eslint-disable @next/next/no-img-element */

import { formatNumberToPriceText } from '@/lib/utils';
import { Product } from '@/models/Product';
import {
  CameraIcon,
  CropIcon,
  FlipHorizontalIcon,
  ImagesIcon,
  MaximizeIcon,
  MinusCircleIcon,
  PlusCircleIcon,
  RotateCwIcon,
  SwitchCameraIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import {
  PHOTO_PRINT_CAPTION_STYLE,
  type PhotoPrintButtonEvent,
  type PhotoPrintDraft,
  type PhotoPrintFilter,
  type PhotoPrintInputChangeEvent,
  type PhotoPrintTextStyle,
} from './PhotoPrintFlowTypes';
import {
  getFilterPreviewClass,
  getPhotoImageStyle,
  getTextStyleClass,
  getTextStyleInline,
  stopPhotoPrintButtonEvent,
} from './PhotoPrintFlowUtils';
import { PhotoFlowFooter, PhotoFlowShell } from './PhotoPrintLayout';
import { isPhotoPrintAspectMismatch, loadImageNaturalSize } from './PhotoPrintPrintSpec';

const PhotoPrintCropOverlay = dynamic(
  () => import('./PhotoPrintCropOverlay').then((mod) => ({ default: mod.PhotoPrintCropOverlay })),
  { ssr: false },
);

function normalizePhotoPrintQuantity(quantity: unknown) {
  const numericQuantity = Number(quantity);
  return Number.isFinite(numericQuantity) ? Math.max(1, numericQuantity) : 1;
}

export function PhotoSourceSelector({
  pageIsApp,
  selectedProduct,
  currentImageUrl,
  onBack,
  onOpenCamera,
  onOpenGallery,
  onSelectGalleryImage,
}: {
  pageIsApp?: boolean;
  selectedProduct: Product;
  currentImageUrl?: string | null;
  onBack: (event?: PhotoPrintButtonEvent) => void;
  onOpenCamera: (event?: PhotoPrintButtonEvent) => void;
  onOpenGallery: (event?: PhotoPrintButtonEvent) => void;
  onSelectGalleryImage: (imageDataUrl: string) => void;
}) {
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const [galleryError, setGalleryError] = useState<string | null>(null);

  const handleGalleryClick = useCallback(
    (event: PhotoPrintButtonEvent) => {
      stopPhotoPrintButtonEvent(event);
      setGalleryError(null);

      if (pageIsApp) {
        onOpenGallery(event);
        return;
      }

      galleryInputRef.current?.click();
    },
    [onOpenGallery, pageIsApp],
  );

  const handleGalleryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';

      if (!file) return;

      if (!file.type.startsWith('image/')) {
        setGalleryError('Lütfen bir fotoğraf dosyası seçin.');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          setGalleryError('Fotoğraf okunamadı. Lütfen tekrar deneyin.');
          return;
        }

        const dataUrl = reader.result;
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth === 0 || img.naturalHeight === 0) {
            setGalleryError(
              'Bu fotoğraf formatı desteklenmiyor. Lütfen farklı bir fotoğraf seçin.',
            );
            return;
          }
          setGalleryError(null);
          onSelectGalleryImage(dataUrl);
        };
        img.onerror = () => {
          setGalleryError('Bu fotoğraf formatı desteklenmiyor. Lütfen farklı bir fotoğraf seçin.');
        };
        img.src = dataUrl;
      };
      reader.onerror = () => {
        setGalleryError('Fotoğraf okunamadı. Lütfen tekrar deneyin.');
      };
      reader.readAsDataURL(file);
    },
    [onSelectGalleryImage],
  );

  const handleCameraClick = useCallback(
    (event: PhotoPrintButtonEvent) => {
      stopPhotoPrintButtonEvent(event);
      onOpenCamera(event);
    },
    [onOpenCamera],
  );

  return (
    <PhotoFlowShell
      title={selectedProduct.title}
      step="source"
      onBack={() => onBack()}
      className="w-full lg:items-center"
    >
      <div className="w-full rounded-2xl bg-white p-5 sm:p-6 lg:mx-auto lg:max-w-5xl lg:p-8">
        <h3 className="text-base font-semibold text-hzc_beige_900 sm:text-lg lg:text-2xl">
          Fotoğrafını seç
        </h3>
        <p className="mt-3 max-w-2xl text-xs leading-5 text-hzc_beige_800 sm:text-sm lg:text-base lg:leading-7">
          Çiçeğinle birlikte göndereceğimiz fotoğrafı yüklemek için bir seçenek seç.
        </p>

        {currentImageUrl ? (
          <div className="mt-4 flex flex-col gap-2">
            <span className="text-xs font-semibold text-hzc_beige_700">Mevcut fotoğraf</span>
            <div className="w-full max-w-[200px] overflow-hidden rounded-xl bg-hzc_beige_100">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={currentImageUrl}
                alt="Mevcut fotoğraf baskısı"
                className="aspect-square w-full object-cover"
              />
            </div>
          </div>
        ) : null}

        <div className="mt-5 grid gap-3 lg:grid-cols-2 lg:gap-5">
          <SourceOptionButton
            icon={<CameraIcon className="h-7 w-7 text-hzc_purple_500" />}
            title="Kameradan Çek"
            description="Şimdi yeni fotoğraf çek"
            onClick={handleCameraClick}
          />

          <SourceOptionButton
            icon={<ImagesIcon className="h-7 w-7 text-hzc_purple_500" />}
            title="Galeriden Yükle"
            description="Mevcut bir fotoğrafı seçin"
            onClick={handleGalleryClick}
          />
        </div>
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleGalleryChange}
        />
        {galleryError && <p className="mt-3 text-sm font-semibold text-red-500">{galleryError}</p>}

        <div className="mt-4 rounded-lg bg-hzc_beige_200 p-3 sm:p-4 lg:mt-6 lg:p-5">
          <p className="text-sm font-semibold text-hzc_beige_900 lg:text-base">
            Daha iyi sonuç için
          </p>
          <p className="mt-1 text-xs leading-5 text-hzc_beige_800 sm:text-sm lg:text-base lg:leading-7">
            Yüksek çözünürlüklü fotoğraf <strong>(en az 1200×1800px)</strong> seç. Baskı kağıdı
            10×15 cm olduğu için dikey <strong>2:3</strong> oran öneriyoruz.
          </p>
        </div>
      </div>
    </PhotoFlowShell>
  );
}

export function PhotoCameraCapture({
  selectedProduct,
  onBack,
  onCapture,
}: {
  selectedProduct: Product;
  onBack: () => void;
  onCapture: (imageDataUrl: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraLoading, setIsCameraLoading] = useState(true);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function openCamera() {
      try {
        setIsCameraLoading(true);
        setCameraError(null);

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('unsupported');
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: facingMode } },
        });

        if (!isMounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        if (navigator.mediaDevices.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoInputs = devices.filter((d) => d.kind === 'videoinput');
          if (isMounted) setHasMultipleCameras(videoInputs.length > 1);
        }
      } catch (error) {
        if (isMounted) {
          if (error instanceof Error && error.message === 'unsupported') {
            setCameraError('Bu cihazda kamera desteklenmiyor.');
          } else if (error instanceof DOMException && error.name === 'NotFoundError') {
            setCameraError('Kamera bulunamadı.');
          } else if (error instanceof DOMException && error.name === 'NotAllowedError') {
            setCameraError('Kamera açılamadı. Lütfen kamera iznini kontrol edin.');
          } else {
            setCameraError('Kamera açılamadı. Lütfen kamera iznini kontrol edin.');
          }
        }
      } finally {
        if (isMounted) {
          setIsCameraLoading(false);
        }
      }
    }

    openCamera();

    return () => {
      isMounted = false;
      stopCamera();
    };
  }, [facingMode, stopCamera]);

  const handleBack = useCallback(() => {
    stopCamera();
    onBack();
  }, [onBack, stopCamera]);

  const handleFlip = useCallback(() => {
    stopCamera();
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  }, [stopCamera]);

  const handleCapture = useCallback(
    (event: PhotoPrintButtonEvent) => {
      stopPhotoPrintButtonEvent(event);

      const video = videoRef.current;
      if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
        setCameraError('Fotoğraf alınamadı. Lütfen tekrar deneyin.');
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        setCameraError('Fotoğraf hazırlanamadı. Lütfen tekrar deneyin.');
        return;
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.92);
      stopCamera();
      onCapture(imageDataUrl);
    },
    [onCapture, stopCamera],
  );

  return (
    <PhotoFlowShell title={selectedProduct.title} step="source" onBack={handleBack}>
      <div className="w-full overflow-hidden rounded-2xl bg-black lg:mx-auto lg:max-w-4xl">
        <div className="relative aspect-[3/4] w-full lg:aspect-video">
          {isCameraLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-hzc_beige_900 text-sm font-semibold text-white">
              Kamera açılıyor...
            </div>
          )}

          {cameraError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-hzc_beige_900 p-6 text-center text-sm font-semibold text-white">
              {cameraError}
            </div>
          ) : (
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="h-full w-full object-cover"
            />
          )}

          {hasMultipleCameras && !cameraError && !isCameraLoading && (
            <button
              type="button"
              onClick={handleFlip}
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
              aria-label="Kamerayı çevir"
            >
              <SwitchCameraIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center py-2">
        <button
          type="button"
          onPointerDown={stopPhotoPrintButtonEvent}
          onMouseDown={stopPhotoPrintButtonEvent}
          onClickCapture={handleCapture}
          onClick={stopPhotoPrintButtonEvent}
          disabled={Boolean(cameraError) || isCameraLoading}
          className="h-16 w-16 rounded-full border-4 border-white bg-hzc_purple_500 shadow-md disabled:opacity-50"
          aria-label="Fotoğraf çek"
        />
      </div>
    </PhotoFlowShell>
  );
}

export function PhotoCustomizePrompt({
  selectedProduct,
  imageUrl,
  onBack,
  onCustomize,
  onSkip,
  onCropNow,
}: {
  selectedProduct: Product;
  imageUrl: string;
  onBack: () => void;
  onCustomize: (event: PhotoPrintButtonEvent) => void;
  onSkip: (event: PhotoPrintButtonEvent) => void;
  onCropNow?: (event: PhotoPrintButtonEvent) => void;
}) {
  const [promptPhase, setPromptPhase] = useState<'checking' | 'aspectWarning' | 'customize'>(
    'checking',
  );

  useEffect(() => {
    let cancelled = false;

    loadImageNaturalSize(imageUrl)
      .then(({ width, height }) => {
        if (cancelled) return;
        setPromptPhase(
          isPhotoPrintAspectMismatch(width, height, '2:3') ? 'aspectWarning' : 'customize',
        );
      })
      .catch(() => {
        if (!cancelled) setPromptPhase('customize');
      });

    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  const showAspectWarning = promptPhase === 'aspectWarning';
  const showCustomize = promptPhase === 'customize';

  return (
    <PhotoFlowShell
      title={selectedProduct.title}
      step="customizePrompt"
      onBack={onBack}
      className="w-full lg:items-center"
    >
      <div className="pointer-events-none w-full rounded-2xl bg-white p-5 opacity-50 sm:p-6 lg:mx-auto lg:max-w-5xl lg:p-8">
        <h3 className="text-base font-semibold text-hzc_beige_900 sm:text-lg lg:text-2xl">
          Fotoğrafını seç
        </h3>
        <p className="mt-3 max-w-2xl text-xs leading-5 text-hzc_beige_800 sm:text-sm lg:text-base lg:leading-7">
          Çiçeğinle birlikte göndereceğimiz fotoğrafı yüklemek için bir seçenek seç.
        </p>

        <div className="mt-5 grid gap-3 lg:grid-cols-2 lg:gap-5">
          <SourceOptionButton
            icon={<CameraIcon className="h-7 w-7 text-hzc_purple_500" />}
            title="Kameradan Çek"
            description="Şimdi yeni fotoğraf çek"
            onClick={stopPhotoPrintButtonEvent}
          />

          <SourceOptionButton
            icon={<ImagesIcon className="h-7 w-7 text-hzc_purple_500" />}
            title="Galeriden Yükle"
            description="Mevcut bir fotoğrafı seçin"
            onClick={stopPhotoPrintButtonEvent}
          />
        </div>

        <div className="mt-4 rounded-lg bg-hzc_beige_200 p-3 sm:p-4 lg:mt-6 lg:p-5">
          <p className="text-sm font-semibold text-hzc_beige_900 lg:text-base">
            Daha iyi sonuç için
          </p>
          <p className="mt-1 text-xs leading-5 text-hzc_beige_800 sm:text-sm lg:text-base lg:leading-7">
            Yüksek çözünürlüklü fotoğraf <strong>(en az 1200×1800px)</strong> seç. Baskı kağıdı
            10×15 cm olduğu için dikey <strong>2:3</strong> oran öneriyoruz.
          </p>
        </div>
      </div>
      <div className="fixed inset-0 z-[60] bg-black/50" aria-hidden />

      {showAspectWarning && (
        <div className="fixed left-4 right-4 top-1/2 z-[70] max-h-[min(90vh,520px)] -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:left-6 sm:right-6 md:left-1/2 md:right-auto md:w-[min(520px,calc(100vw-48px))] md:-translate-x-1/2 md:p-6 lg:w-[560px]">
          <button
            type="button"
            onClick={(event) => {
              stopPhotoPrintButtonEvent(event);
              onBack();
            }}
            className="absolute right-5 top-5 text-hzc_beige_900"
            aria-label="Kapat"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <h3 className="max-w-[310px] text-xl font-semibold leading-7 text-hzc_beige_900 md:max-w-[420px] md:text-2xl md:leading-8">
            Fotoğraf Oranı Uyumsuz
          </h3>
          <p className="mt-4 text-sm leading-5 text-hzc_beige_900 md:text-base md:leading-7">
            Yüklediğiniz fotoğraf baskı kağıdı oranından (10×15 / 2:3) farklı. Kenarlardaki yüzler
            kesilebilir. Devam etmeden önce kırpmanızı öneririz.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onPointerDown={stopPhotoPrintButtonEvent}
              onMouseDown={stopPhotoPrintButtonEvent}
              onClickCapture={onCropNow || onCustomize}
              onClick={stopPhotoPrintButtonEvent}
              className="h-10 rounded-[10px] bg-hzc_purple_500 text-sm font-semibold text-white md:h-11"
            >
              Kırp
            </button>
            <button
              type="button"
              onPointerDown={stopPhotoPrintButtonEvent}
              onMouseDown={stopPhotoPrintButtonEvent}
              onClick={(event) => {
                stopPhotoPrintButtonEvent(event);
                setPromptPhase('customize');
              }}
              className="h-10 rounded-[10px] bg-hzc_purple_300 text-sm font-semibold text-white md:h-11"
            >
              Devam et
            </button>
          </div>
        </div>
      )}

      {showCustomize && (
        <div className="fixed left-4 right-4 top-1/2 z-[70] max-h-[min(90vh,520px)] -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:left-6 sm:right-6 md:left-1/2 md:right-auto md:w-[min(520px,calc(100vw-48px))] md:-translate-x-1/2 md:p-6 lg:w-[560px]">
          <button
            type="button"
            onClick={(event) => {
              stopPhotoPrintButtonEvent(event);
              onBack();
            }}
            className="absolute right-5 top-5 text-hzc_beige_900"
            aria-label="Kapat"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <h3 className="max-w-[310px] text-xl font-semibold leading-7 text-hzc_beige_900 md:max-w-[420px] md:text-2xl md:leading-8">
            Fotoğrafınızı Özelleştirmek İster Misiniz?
          </h3>
          <p className="mt-4 text-sm leading-5 text-hzc_beige_900 md:text-base md:leading-7">
            Dilerseniz fotoğrafınızı kırpabilir, yakınlaştırabilir ya da çeşitli filtreler ile
            istediğiniz gibi özelleştirebilirsiniz.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onPointerDown={stopPhotoPrintButtonEvent}
              onMouseDown={stopPhotoPrintButtonEvent}
              onClickCapture={onCustomize}
              onClick={stopPhotoPrintButtonEvent}
              className="h-10 rounded-[10px] bg-hzc_purple_500 text-sm font-semibold text-white md:h-11"
            >
              Evet
            </button>
            <button
              type="button"
              onPointerDown={stopPhotoPrintButtonEvent}
              onMouseDown={stopPhotoPrintButtonEvent}
              onClickCapture={onSkip}
              onClick={stopPhotoPrintButtonEvent}
              className="h-10 rounded-[10px] bg-hzc_purple_300 text-sm font-semibold text-white md:h-11"
            >
              Hayır
            </button>
          </div>
        </div>
      )}
    </PhotoFlowShell>
  );
}

export function PhotoCropEditor({
  draft,
  imageUrl,
  selectedProduct,
  onBack,
  onChange,
  onNext,
  onChangePhoto,
  initialOpenCropOverlay = false,
}: {
  draft: PhotoPrintDraft;
  imageUrl: string;
  selectedProduct: Product;
  onBack: () => void;
  onChange: (partial: Partial<PhotoPrintDraft>) => void;
  onNext: () => void;
  onChangePhoto?: () => void;
  initialOpenCropOverlay?: boolean;
}) {
  const [aspectMismatch, setAspectMismatch] = useState(false);
  const [isCropOverlayOpen, setIsCropOverlayOpen] = useState(initialOpenCropOverlay);

  const sourceImageUrl = draft.originalImage || imageUrl;

  useEffect(() => {
    let cancelled = false;

    // Preview image: after overlay apply this is already 2:3, so banner can hide.
    loadImageNaturalSize(imageUrl)
      .then(({ width, height }) => {
        if (cancelled) return;
        setAspectMismatch(
          isPhotoPrintAspectMismatch(width, height, draft.crop.aspectRatio, draft.crop.rotation),
        );
      })
      .catch(() => {
        if (!cancelled) setAspectMismatch(false);
      });

    return () => {
      cancelled = true;
    };
  }, [draft.crop.aspectRatio, draft.crop.rotation, imageUrl]);

  const updateZoom = useCallback(
    (event: PhotoPrintInputChangeEvent) => {
      onChange({ zoom: Number(event.target.value) });
    },
    [onChange],
  );

  const applyFit = useCallback(() => {
    onChange({
      editedImage: draft.originalImage || imageUrl,
      crop: { ...draft.crop, aspectRatio: 'fit' },
      zoom: 1,
    });
  }, [draft.crop, draft.originalImage, imageUrl, onChange]);

  const handleCropApply = useCallback(
    (croppedDataUrl: string) => {
      onChange({
        editedImage: croppedDataUrl,
        crop: {
          ...draft.crop,
          aspectRatio: '2:3',
          rotation: 0,
          flipped: false,
        },
        zoom: 1,
      });
      setIsCropOverlayOpen(false);
    },
    [draft.crop, onChange],
  );

  const showAspectBanner = aspectMismatch && draft.crop.aspectRatio === '2:3';

  return (
    <>
      <PhotoFlowShell
        title="Fotoğrafı Kırp"
        step="crop"
        onBack={onBack}
        footer={
          <PhotoFlowFooter backLabel="Geri" nextLabel="İleri" onBack={onBack} onNext={onNext} />
        }
        className="w-full lg:grid lg:grid-cols-[minmax(420px,1fr)_420px] lg:items-start lg:gap-8"
      >
        <div className="w-full rounded-2xl bg-white p-4 sm:p-5 lg:p-6">
          <div className="relative rounded-2xl bg-hzc_beige_200 p-4 sm:p-6 lg:p-10">
            <div className="relative mx-auto aspect-[2/3] w-full max-w-[300px] overflow-hidden bg-white sm:max-w-[320px] lg:max-w-[420px]">
              <img
                src={imageUrl}
                alt={selectedProduct.title}
                className="h-full w-full object-cover"
                style={getPhotoImageStyle(draft)}
              />
              <div className="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3 border border-hzc_green/80">
                {Array.from({ length: 9 }).map((_, index) => (
                  <div key={index} className="border border-hzc_green/40" />
                ))}
              </div>
            </div>
            {onChangePhoto && (
              <button
                type="button"
                onClick={onChangePhoto}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-red-500/80"
                aria-label="Fotoğrafı değiştir"
              >
                <Trash2Icon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 pb-4 lg:mt-0 lg:pb-0">
          {showAspectBanner && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
              <p className="text-sm font-semibold text-amber-950">Kenarlar kırpılabilir</p>
              <p className="mt-1 text-xs leading-5 text-amber-900 sm:text-sm">
                Fotoğraf oranı 10×15 baskı kağıdından (2:3) farklı. Yüzleri korumak için Kırp ile
                alanı seçin veya Sığdır ile tamamını koruyun.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setIsCropOverlayOpen(true)}
                  className="h-10 rounded-full bg-hzc_green px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                >
                  Kırp
                </button>
                <button
                  type="button"
                  onClick={applyFit}
                  className="h-10 rounded-full border border-amber-400 bg-white px-4 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-100"
                >
                  Sığdır
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-4 gap-2 rounded-2xl bg-white p-4 sm:p-5 lg:grid-cols-2 lg:p-4">
            <CropActionButton
              icon={<RotateCwIcon className="h-5 w-5" />}
              label="Döndür"
              onClick={() =>
                onChange({
                  crop: { ...draft.crop, rotation: (draft.crop.rotation + 90) % 360 },
                })
              }
            />
            <CropActionButton
              icon={<FlipHorizontalIcon className="h-5 w-5" />}
              label="Yansıt"
              onClick={() =>
                onChange({
                  crop: { ...draft.crop, flipped: !draft.crop.flipped },
                })
              }
            />
            <CropActionButton
              icon={<CropIcon className="h-5 w-5" />}
              label="Kırp"
              onClick={() => setIsCropOverlayOpen(true)}
            />
            <CropActionButton
              icon={<MaximizeIcon className="h-5 w-5" />}
              label="Sığdır"
              onClick={applyFit}
            />
          </div>

          <SliderRow
            label="Yakınlaştırma"
            value={draft.zoom}
            min={1}
            max={2}
            step={0.05}
            display={`${draft.zoom.toFixed(1)}x`}
            onChange={updateZoom}
          />
        </div>
      </PhotoFlowShell>

      {isCropOverlayOpen && (
        <PhotoPrintCropOverlay
          imageUrl={sourceImageUrl}
          draft={draft}
          onCancel={() => setIsCropOverlayOpen(false)}
          onApply={handleCropApply}
        />
      )}
    </>
  );
}

export function PhotoFilterEditor({
  draft,
  imageUrl,
  selectedProduct,
  onBack,
  onChange,
  onNext,
  onChangePhoto,
  showCaptionDialog = true,
}: {
  draft: PhotoPrintDraft;
  imageUrl: string;
  selectedProduct: Product;
  onBack: () => void;
  onChange: (partial: Partial<PhotoPrintDraft>) => void;
  onNext: () => void;
  onChangePhoto?: () => void;
  showCaptionDialog?: boolean;
}) {
  const filterOptions: Array<{ id: PhotoPrintFilter; label: string }> = [
    { id: 'original', label: 'Orijinal' },
    { id: 'vintage', label: 'Vintage' },
    { id: 'warm', label: 'Sıcak' },
    { id: 'cool', label: 'Soğuk' },
    { id: 'bw', label: 'B&W' },
  ];

  return (
    <PhotoFlowShell
      title="Filtre Uygula"
      step="filter"
      onBack={onBack}
      footer={
        <PhotoFlowFooter
          backLabel="Geri"
          nextLabel="Önizlemeyi Gör"
          onBack={onBack}
          onNext={onNext}
        />
      }
      className="w-full lg:grid lg:grid-cols-[minmax(420px,1fr)_440px] lg:items-start lg:gap-8"
    >
      <div className="w-full rounded-2xl bg-white p-4 sm:p-5 lg:p-6">
        <div className="relative">
          <PhotoImageFrame draft={draft} imageUrl={imageUrl} />
          {onChangePhoto && (
            <button
              type="button"
              onClick={onChangePhoto}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-red-500/80"
              aria-label="Fotoğrafı değiştir"
            >
              <Trash2Icon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-4 lg:mt-0">
        <div className="rounded-2xl bg-white p-4 sm:p-5 lg:p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-hzc_beige_700">
            Filtreler
          </p>
          <div className="grid grid-cols-5 gap-2 lg:grid-cols-3 lg:gap-3">
            {filterOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={(event) => {
                  stopPhotoPrintButtonEvent(event);
                  onChange({ filter: option.id });
                }}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-1.5 text-[10px] font-semibold sm:gap-2 sm:p-2 sm:text-xs lg:min-w-0 ${
                  draft.filter === option.id
                    ? 'border-hzc_purple_500 text-hzc_purple_500'
                    : 'border-transparent text-hzc_beige_800'
                }`}
              >
                <span className="leading-none">{option.label}</span>
                <img
                  src={imageUrl}
                  alt={option.label}
                  className={`h-12 w-full rounded-md bg-hzc_beige_100 object-contain sm:h-14 lg:h-20 ${getFilterPreviewClass(
                    option.id,
                  )}`}
                />
              </button>
            ))}
          </div>
        </div>

        <SliderRow
          label="Parlaklık"
          value={draft.brightness}
          min={-20}
          max={20}
          step={1}
          display={draft.brightness > 0 ? `+${draft.brightness}` : String(draft.brightness)}
          onChange={(event) => onChange({ brightness: Number(event.target.value) })}
        />
        <SliderRow
          label="Kontrast"
          value={draft.contrast}
          min={-20}
          max={20}
          step={1}
          display={draft.contrast > 0 ? `+${draft.contrast}` : String(draft.contrast)}
          onChange={(event) => onChange({ contrast: Number(event.target.value) })}
        />
        <SliderRow
          label="Doygunluk"
          value={draft.saturation}
          min={-20}
          max={20}
          step={1}
          display={draft.saturation > 0 ? `+${draft.saturation}` : String(draft.saturation)}
          onChange={(event) => onChange({ saturation: Number(event.target.value) })}
        />

        {showCaptionDialog && <CaptionEditor draft={draft} onChange={onChange} />}
      </div>
    </PhotoFlowShell>
  );
}

export function PhotoPreview({
  draft,
  imageUrl,
  selectedProduct,
  onBack,
  onChange,
  onConfirm,
  isSubmitting = false,
  onChangePhoto,
  showCaptionDialog = true,
  confirmLabel,
}: {
  draft: PhotoPrintDraft;
  imageUrl: string;
  selectedProduct: Product;
  onBack: () => void;
  onChange: (partial: Partial<PhotoPrintDraft>) => void;
  onConfirm: (event: PhotoPrintButtonEvent) => void;
  isSubmitting?: boolean;
  onChangePhoto?: () => void;
  showCaptionDialog?: boolean;
  confirmLabel?: string;
}) {
  const filterLabel = {
    original: 'Orijinal filtre',
    vintage: 'Vintage filtre',
    warm: 'Sıcak filtre',
    cool: 'Soğuk filtre',
    bw: 'Siyah beyaz filtre',
  }[draft.filter];

  const textStyleLabel = {
    handwriting: 'El yazısı',
    classic: 'Klasik',
    typewriter: 'Daktilo',
  }[draft.textStyle];

  const defaultConfirmLabel = draft.isAddedToBasket
    ? 'Düzenlemeyi Kaydet'
    : 'Onayla ve Sepete Ekle';
  const resolvedConfirmLabel = confirmLabel || defaultConfirmLabel;

  return (
    <PhotoFlowShell
      title="Önizleme"
      step="preview"
      onBack={onBack}
      footer={
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onPointerDown={stopPhotoPrintButtonEvent}
            onMouseDown={stopPhotoPrintButtonEvent}
            onClickCapture={onConfirm}
            onClick={stopPhotoPrintButtonEvent}
            disabled={isSubmitting}
            className="h-12 rounded-xl bg-[#D2F25C] text-sm font-bold text-hzc_purple_500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting
              ? 'İşleniyor...'
              : confirmLabel
                ? `✓ ${resolvedConfirmLabel}`
                : `✓ ${resolvedConfirmLabel} (${formatNumberToPriceText(selectedProduct.price)} TL)`}
          </button>
          <button
            type="button"
            onClick={(event) => {
              stopPhotoPrintButtonEvent(event);
              onBack();
            }}
            className="h-12 rounded-xl bg-hzc_purple_300 text-sm font-bold text-white"
          >
            Düzenle
          </button>
        </div>
      }
      className="w-full lg:grid lg:grid-cols-[minmax(420px,1fr)_440px] lg:items-start lg:gap-8"
    >
      <div className="flex w-full flex-col gap-4">
        <div>
          <h3 className="text-lg font-semibold text-hzc_beige_900 lg:text-2xl">
            Fotoğrafın böyle gözükecek
          </h3>
          <p className="mt-1 text-sm text-hzc_beige_700 lg:text-base">
            Onaylayarak siparişe ekleyebilirsin
          </p>
        </div>

        <div className="rounded-2xl bg-white p-4 sm:p-5 lg:p-6">
          <div className="relative">
            <PhotoImageFrame draft={draft} imageUrl={imageUrl} shadow />
            {onChangePhoto && (
              <button
                type="button"
                onClick={onChangePhoto}
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-red-500/80"
                aria-label="Fotoğrafı değiştir"
              >
                <Trash2Icon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4 rounded-2xl bg-white p-4 sm:p-5 lg:mt-[92px] lg:p-4">
        <div className="flex items-start justify-between gap-3 border-b border-hzc_beige_200 pb-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-hzc_beige_900 sm:text-base">
              {selectedProduct.title}
            </p>
            <p className="mt-1 text-xs text-hzc_beige_700 sm:text-sm">
              {filterLabel}
              {showCaptionDialog && ` · ${draft.sendWithoutText ? 'Yazısız' : textStyleLabel}`}
            </p>
          </div>
          <span className="shrink-0 text-base font-bold text-hzc_purple_500 sm:text-lg">
            {formatNumberToPriceText(selectedProduct.price)} TL
          </span>
        </div>

        {!confirmLabel && (
          <div className="flex items-center justify-between text-sm font-semibold text-hzc_beige_900">
            Adet
            <div className="flex items-center gap-3 text-hzc_purple_500">
              <button
                type="button"
                onClick={(event) => {
                  stopPhotoPrintButtonEvent(event);
                  onChange({
                    quantity: Math.max(1, normalizePhotoPrintQuantity(draft.quantity) - 1),
                  });
                }}
                aria-label="Azalt"
              >
                <MinusCircleIcon className="h-5 w-5" />
              </button>
              <span className="min-w-4 text-center text-hzc_beige_900">
                {normalizePhotoPrintQuantity(draft.quantity)}
              </span>
              <button
                type="button"
                onClick={(event) => {
                  stopPhotoPrintButtonEvent(event);
                  onChange({ quantity: normalizePhotoPrintQuantity(draft.quantity) + 1 });
                }}
                aria-label="Artır"
              >
                <PlusCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-[#F5E6A8] bg-[#FFF7DD] p-4">
          <p className="text-sm font-semibold text-hzc_beige_900">⚠️ Basıma hazır</p>
          <p className="mt-2 text-sm leading-5 text-hzc_beige_800">
            {confirmLabel ? (
              <>Onayladıktan sonra fotoğraf siparişte güncellenir.</>
            ) : (
              <>
                Onayladıktan sonra <strong>fotoğraf ve not değişikliği yapılamaz.</strong> Sepette
                tekrar düzenleyebilirsin.
              </>
            )}
          </p>
        </div>
      </div>
    </PhotoFlowShell>
  );
}

function SourceOptionButton({
  icon,
  title,
  description,
  onClick,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  onClick: (event: PhotoPrintButtonEvent) => void;
}) {
  return (
    <button
      type="button"
      onPointerDown={stopPhotoPrintButtonEvent}
      onMouseDown={stopPhotoPrintButtonEvent}
      onClickCapture={onClick}
      onClick={stopPhotoPrintButtonEvent}
      className="flex w-full items-center gap-3 rounded-lg border border-hzc_beige_300 bg-white p-3 text-left transition-colors duration-300 hover:bg-hzc_beige_100 sm:p-4 lg:min-h-[112px] lg:gap-4 lg:p-5"
    >
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-hzc_beige_100 lg:h-16 lg:w-16">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-sm font-semibold text-hzc_beige_900 sm:text-base lg:text-lg">
          {title}
        </span>
        <span className="text-xs text-hzc_beige_800 sm:text-sm lg:text-base">{description}</span>
      </span>
    </button>
  );
}

function CropActionButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        stopPhotoPrintButtonEvent(event);
        onClick();
      }}
      className="flex min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-lg border border-hzc_beige_300 bg-hzc_beige_100 px-1 py-2 text-[10px] font-semibold text-hzc_beige_900 sm:min-h-[76px] sm:gap-2 sm:text-xs lg:min-h-[96px] lg:text-sm"
    >
      {icon}
      {label}
    </button>
  );
}

function CaptionEditor({
  draft,
  onChange,
}: {
  draft: PhotoPrintDraft;
  onChange: (partial: Partial<PhotoPrintDraft>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl bg-white p-4 sm:p-5">
        <label className="text-sm font-medium text-hzc_beige_700" htmlFor="photo-caption">
          Alt Yazı
        </label>
        <textarea
          id="photo-caption"
          maxLength={40}
          value={draft.caption}
          disabled={draft.sendWithoutText}
          onChange={(event) => onChange({ caption: event.target.value })}
          className={`mt-2 min-h-[96px] w-full rounded-xl border border-hzc_beige_300 bg-white p-3 text-sm outline-none disabled:opacity-50 ${getTextStyleClass(
            draft.textStyle,
          )}`}
          style={getTextStyleInline(draft.textStyle)}
        />
        <p className="mt-1 text-right text-xs font-semibold text-hzc_beige_900">
          {draft.caption.length} / 40
        </p>
      </div>

      <div className="rounded-2xl bg-white p-4 sm:p-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-hzc_beige_700">
          Yazı Stili
        </p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { id: PHOTO_PRINT_CAPTION_STYLE.HANDWRITING, label: 'El Yazısı' },
            { id: PHOTO_PRINT_CAPTION_STYLE.CLASSIC, label: 'Klasik' },
            { id: PHOTO_PRINT_CAPTION_STYLE.TYPEWRITER, label: 'Daktilo' },
          ].map((style) => (
            <button
              key={style.id}
              type="button"
              onClick={(event) => {
                stopPhotoPrintButtonEvent(event);
                onChange({ textStyle: style.id as PhotoPrintTextStyle });
              }}
              className={`h-10 rounded-lg text-sm font-semibold ${
                draft.textStyle === style.id
                  ? 'bg-hzc_green text-hzc_purple_500'
                  : 'bg-hzc_beige_100 text-hzc_beige_900'
              } ${getTextStyleClass(style.id as PhotoPrintTextStyle)}`}
              style={getTextStyleInline(style.id as PhotoPrintTextStyle)}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center justify-between rounded-2xl bg-white p-4 text-sm font-semibold text-hzc_beige_900 sm:p-5">
        Yazı eklemeden gönder
        <input
          type="checkbox"
          checked={draft.sendWithoutText}
          onChange={(event) => onChange({ sendWithoutText: event.target.checked })}
          className="h-5 w-5 accent-hzc_purple_500"
        />
      </label>
    </div>
  );
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (event: PhotoPrintInputChangeEvent) => void;
}) {
  return (
    <div className="rounded-2xl bg-white p-4 shadow-none sm:p-5">
      <div className="mb-2 flex items-center justify-between text-sm font-semibold text-hzc_beige_900">
        <span>{label}</span>
        <span>{display}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        className="w-full accent-hzc_purple_500"
      />
    </div>
  );
}

function PhotoImageFrame({
  imageUrl,
  draft,
  shadow = false,
  compact = false,
}: {
  imageUrl: string;
  draft?: PhotoPrintDraft;
  shadow?: boolean;
  compact?: boolean;
}) {
  const captionEnabled = draft?.selectedProduct?.showCaptionDialog !== false;
  const caption = !captionEnabled || draft?.sendWithoutText ? '' : draft?.caption || '';

  return (
    <div className={`rounded-2xl bg-hzc_beige_200 ${compact ? 'p-4' : 'p-5 sm:p-6 lg:p-10'}`}>
      <div
        className={`mx-auto w-full bg-white p-2.5 sm:p-3 lg:p-4 ${
          compact ? 'max-w-[220px]' : 'max-w-[min(100%,280px)] sm:max-w-[300px] lg:max-w-[380px]'
        } ${shadow ? 'shadow-xl' : ''}`}
      >
        <div className="aspect-[2/3] w-full overflow-hidden bg-hzc_beige_100">
          <img
            src={imageUrl}
            alt="Fotoğraf önizleme"
            className="h-full w-full object-cover"
            style={getPhotoImageStyle(draft)}
          />
        </div>
        <p
          className={`min-h-8 px-1 pt-3 text-center text-sm leading-5 text-hzc_beige_900 lg:text-base lg:leading-6 ${
            caption ? '' : 'invisible'
          } ${getTextStyleClass(draft?.textStyle || 'handwriting')}`}
          style={getTextStyleInline(draft?.textStyle || 'handwriting')}
        >
          {caption || ' '}
        </p>
      </div>
    </div>
  );
}
